/**
 * Cliente API para el módulo de Finanzas
 * Maneja todas las peticiones al backend /api/finanzas/*
 */
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Crear instancia de axios con configuración base
const finanzasApi = axios.create({
  baseURL: `${API_BASE}/api/finanzas`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor para añadir token de autenticación
finanzasApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor para manejar errores
finanzasApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expirado o inválido
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ════════════════════════════════════════════════════════════════
//                         ENTIDADES
// ════════════════════════════════════════════════════════════════

export const entidadesApi = {
  listar: () => finanzasApi.get('/entidades'),
  obtener: (id) => finanzasApi.get(`/entidades/${id}`),
  crear: (data) => finanzasApi.post('/entidades', data),
  actualizar: (id, data) => finanzasApi.put(`/entidades/${id}`, data),
  eliminar: (id) => finanzasApi.delete(`/entidades/${id}`)
};

// ════════════════════════════════════════════════════════════════
//                         CUENTAS
// ════════════════════════════════════════════════════════════════

export const cuentasApi = {
  listar: (entidadId = null) => finanzasApi.get('/cuentas', { params: { entidad_id: entidadId } }),
  obtener: (id) => finanzasApi.get(`/cuentas/${id}`),
  crear: (data) => finanzasApi.post('/cuentas', data),
  actualizar: (id, data) => finanzasApi.put(`/cuentas/${id}`, data),
  eliminar: (id) => finanzasApi.delete(`/cuentas/${id}`)
};

// ════════════════════════════════════════════════════════════════
//                         CATEGORÍAS
// ════════════════════════════════════════════════════════════════

export const categoriasApi = {
  listar: (tipo = null) => finanzasApi.get('/categorias', { params: { tipo } }),
  obtenerArbol: () => finanzasApi.get('/categorias/arbol'),
  crear: (data) => finanzasApi.post('/categorias', data),
  actualizar: (id, data) => finanzasApi.put(`/categorias/${id}`, data),
  eliminar: (id) => finanzasApi.delete(`/categorias/${id}`),
  exportar: () => finanzasApi.get('/export/categorias', { responseType: 'blob' }),
  importar: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return finanzasApi.post('/import/categorias', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// ════════════════════════════════════════════════════════════════
//                         MOVIMIENTOS
// ════════════════════════════════════════════════════════════════

export const movimientosApi = {
  listar: (params = {}) => finanzasApi.get('/movimientos', { params }),
  obtener: (id) => finanzasApi.get(`/movimientos/${id}`),
  crear: (data) => finanzasApi.post('/movimientos', data),
  actualizar: (id, data) => finanzasApi.put(`/movimientos/${id}`, data),
  eliminar: (id) => finanzasApi.delete(`/movimientos/${id}`),
  reconciliar: (id) => finanzasApi.put(`/movimientos/${id}/reconciliar`),
  exportar: (params = {}) => finanzasApi.get('/export/movimientos', { 
    params, 
    responseType: 'blob' 
  })
};

// ════════════════════════════════════════════════════════════════
//                         REPORTES Y DASHBOARD
// ════════════════════════════════════════════════════════════════

export const reportesApi = {
  dashboard: () => finanzasApi.get('/resumen/dashboard'),
  porCategoria: (params = {}) => finanzasApi.get('/resumen/por-categoria', { params })
};

// Exportar el cliente base por si se necesita
export default finanzasApi;
